// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import ErrorBoundary from './components/ErrorBoundary';
import { useNavigate } from 'react-router-dom';
import { initializeFCM, syncFCMToken, setupForegroundNotifications, startTokenHeartbeat } from './utils/fcm';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Contact from './pages/Contact';
import SearchProviders from './pages/SearchProviders';
import ProviderProfile from './pages/ProviderProfile';
import ManageOrders from './pages/ManageOrders';
import ForgotPassword from './pages/ForgotPassword';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import NotFound from './pages/NotFound';
import Blog from './pages/Blog';
import ArticlePage from './pages/ArticlePage';
import AdminArticlePage from './pages/AdminArticlePage';
import Notifications from './components/Notifications';
import './index.css';

const queryClient = new QueryClient();

function AppContent({ chatSocket, setChatSocket }) {
  const navigate = useNavigate();
  const { data: user, isSuccess, error: userError } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/`, { withCredentials: true });
      return response.data;
    },
    retry: false,
  });

  const [showNotificationsPrompt, setShowNotificationsPrompt] = useState(false);
  const [hasPrompted, setHasPrompted] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    console.log('[App] isPWA useEffect, navigator:', window.navigator.standalone);
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsPWA(isStandalone);
  }, []);

  useEffect(() => {
    console.log('[App] FCM useEffect, user:', user, 'isSuccess:', isSuccess, 'userError:', userError);
    let stopHeartbeat;
    let unsubscribe;

    const setupFCM = async () => {
      try {
        if (
          user &&
          !hasPrompted &&
          'Notification' in window &&
          (isPWA || !/iPhone|iPad|iPod/.test(navigator.userAgent))
        ) {
          console.log('[App] Calling initializeFCM');
          initializeFCM(user, setShowNotificationsPrompt, setHasPrompted);
        }

        if ('Notification' in window && Notification.permission === 'denied') {
          console.log('Notification permission denied, clearing cached token');
          localStorage.removeItem('fcmToken');
          localStorage.removeItem('fcmTokenSavedTimestamp');
          localStorage.removeItem('notificationPromptDismissed');
          setHasPrompted(false);
        }

        console.log('[App] Calling syncFCMToken');
        await syncFCMToken(user); // Await async function
        console.log('[App] Calling startTokenHeartbeat');
        stopHeartbeat = await startTokenHeartbeat(user); // Await to get the function
        console.log('[App] stopHeartbeat:', stopHeartbeat);
        console.log('[App] Calling setupForegroundNotifications');
        unsubscribe = setupForegroundNotifications(navigate);
      } catch (err) {
        console.error('[App] Error in useEffect:', err);
      }
    };

    setupFCM();

    return () => {
      console.log('[App] Cleaning up useEffect');
      if (unsubscribe) unsubscribe();
      if (stopHeartbeat) stopHeartbeat();
    };
  }, [user, hasPrompted, isPWA, navigate]);

  useEffect(() => {
    console.log('[App] WebSocket useEffect, isSuccess:', isSuccess, 'user:', user);
    if (!isSuccess) return;
    if (!user) {
      if (chatSocket?.readyState === WebSocket.OPEN) chatSocket.close();
      setChatSocket(null);
      return;
    }
    return () => {
      if (chatSocket?.readyState === WebSocket.OPEN) chatSocket.close();
    };
  }, [user, isSuccess, chatSocket, setChatSocket]);

  console.log('[App] Rendering AppContent, userId:', user?.id, 'showNotificationsPrompt:', showNotificationsPrompt);
  return (
    <ErrorBoundary>
      <div className="App">
        {user?.id && showNotificationsPrompt && 'Notification' in window && (
          <section className="py-4 px-4 max-w-3xl mx-auto">
            <div className="relative bg-gray-800 rounded-lg border border-gray-600 p-6 animate-fade-in">
              <button
                onClick={() => {
                  setShowNotificationsPrompt(false);
                  setHasPrompted(true);
                  localStorage.setItem('notificationPromptDismissed', 'true');
                }}
                className="absolute top-2 right-2 text-gray-400 hover:text-white text-xl"
                aria-label="Close notification prompt"
              >
                ×
              </button>
              <h2 className="text-2xl font-bold text-white mb-2">Enable Notifications</h2>
              <p className="text-gray-400 mb-4">
                Stay updated with service requests, acceptances, and admin messages! 📩
                {/iPhone|iPad|iPod/.test(navigator.userAgent) && !isPWA ? (
                  <span className="block text-sm text-yellow-400 mt-2">
                    Add SErraND to your home screen to enable notifications on iOS.
                  </span>
                ) : null}
              </p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => initializeFCM(user, setShowNotificationsPrompt, setHasPrompted)}
                  className="btn btn-green transition-transform duration-200 hover:scale-105"
                  disabled={/iPhone|iPad|iPod/.test(navigator.userAgent) && !isPWA}
                >
                  Enable Notifications
                </button>
                <button
                  onClick={() => {
                    setShowNotificationsPrompt(false);
                    setHasPrompted(true);
                    localStorage.setItem('notificationPromptDismissed', 'true');
                  }}
                  className="btn btn-red transition-transform duration-200 hover:scale-105"
                >
                  Not Now
                </button>
              </div>
            </div>
          </section>
        )}
        <Notifications userId={user?.id} />
        <Routes>
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Home />
              </ErrorBoundary>
            }
          />
          <Route path="/profile" element={<Profile chatSocket={chatSocket} setChatSocket={setChatSocket} />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/search" element={<SearchProviders />} />
          <Route path="/provider/:providerId" element={<ProviderProfile />} />
          <Route path="/manage-orders" element={<ManageOrders />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/blog" element={<Blog user={user} />} />
          <Route path="/blog/:id" element={<ArticlePage user={user} />} />
          <Route path="/blog/new" element={<AdminArticlePage user={user} />} />
          <Route path="/blog/edit/:id" element={<AdminArticlePage user={user} />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

function App() {
  const [chatSocket, setChatSocket] = useState(null);

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename="/">
        <AppContent chatSocket={chatSocket} setChatSocket={setChatSocket} />
      </Router>
    </QueryClientProvider>
  );
}

export default App;
